/**
 * useRolesStore — RBAC roles store backed by TanStack Query hooks.
 *
 * Data flows through the real API when available:
 *   GET  /organization/:orgId/roles       -> list
 *   POST /organization/:orgId/roles       -> create
 *   PATCH /organization/:orgId/roles/:key -> update
 *   DELETE /organization/:orgId/roles/:key -> delete
 *
 * Roles fall back to the RBAC_ROLES catalog when the API is unreachable or
 * still loading (the five system roles are seed data every org gets, not
 * fabricated content). Members never fall back to a fixture: they come from
 * the real `useMembers(orgId)` roster, mapped into the lightweight
 * `RbacMember` shape the RBAC matrix uses, and are an honest empty array
 * while loading (#69, #70).
 *
 * The component contract (RolesContextValue) is unchanged from the
 * localStorage-based implementation -- consumers do not need any migration.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { IconShieldUser, type IconProps } from '@shared/icons';
import type { ComponentType } from 'react';
import { useCurrentOrgId } from '@shared/data/use-current-org';
import {
  useRoles as useRolesQuery,
  useCreateRole,
  useUpdateRole as useUpdateRoleMutation,
  useDeleteRole as useDeleteRoleMutation,
} from '../api/role-hooks';
import type { Role as ApiRole } from '../api/role-schemas';
import type { RbacMember, RbacRole } from '../api/schemas';
import { DOMAINS, RBAC_ROLES } from '../lib/constants';
import { useMembers } from '../api/hooks';

type Role = RbacRole;
type Member = RbacMember;

type IconComponent = ComponentType<IconProps>;

/** Mutator payload used by create / duplicate / edit. */
export type RoleDraft = {
  name: string;
  desc: string;
  matrix: Role['matrix'];
};

/** Generate a URL-safe role key from a display name, with a short random tail
 *  so two roles named the same can coexist. */
function generateRoleKey(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const suffix = Math.random().toString(36).slice(2, 5);
  return `${slug || 'role'}-${suffix}`;
}

/** Build an empty matrix with every domain at `none`. */
export function emptyMatrix(): Role['matrix'] {
  const out = {} as Role['matrix'];
  for (const d of DOMAINS) {
    out[d.key] = 'none';
  }
  return out;
}

/* -------------- Icon resolution for API -> component mapping ------------- */

/** Map from RBAC_ROLES keys to their icon components. System roles whose
 *  key matches a fixture role inherit that icon; everything else gets the
 *  generic shield-user glyph. */
const SYSTEM_ICON_MAP: Readonly<Record<string, IconComponent>> = Object.fromEntries(
  RBAC_ROLES.filter((r) => r.system).map((r) => [r.key, r.icon])
);

function resolveIconForRole(key: string, isSystem: boolean): IconComponent {
  if (isSystem) {
    return SYSTEM_ICON_MAP[key] ?? IconShieldUser;
  }
  // Custom roles: check if there is a matching fixture (e.g. "auditor")
  const fixture = RBAC_ROLES.find((r) => r.key === key);
  return fixture?.icon ?? IconShieldUser;
}

/* -------------- Map API Role -> component Role -------------------------  */

function apiRoleToComponentRole(apiRole: ApiRole): Role {
  return {
    key: apiRole.key,
    name: apiRole.name,
    icon: resolveIconForRole(apiRole.key, apiRole.isSystem),
    system: apiRole.isSystem,
    desc: apiRole.description ?? '',
    matrix: { ...apiRole.permissions } as Role['matrix'],
  };
}

/* ------------------------------ Context shape --------------------------- */

export type RolesContextValue = {
  roles: ReadonlyArray<Role>;
  members: ReadonlyArray<Member>;
  /** Create a brand new custom role. Returns the generated key. */
  addRole: (draft: RoleDraft) => string;
  /** Patch a role's name / desc / matrix. No-op for system roles. */
  updateRole: (key: string, draft: RoleDraft) => void;
  /** Delete a custom role outright. Caller is responsible for ensuring no
   *  members hold the role; use `reassignAndDelete` otherwise. */
  deleteRole: (key: string) => void;
  /** Reassign every member of `key` to `targetKey`, then delete `key`. */
  reassignAndDelete: (key: string, targetKey: string) => void;
};

const RolesContext = createContext<RolesContextValue | null>(null);

/* -------------------------------- Provider ------------------------------ */

export function RolesProvider({ children }: { children: ReactNode }): ReactElement {
  const orgId = useCurrentOrgId();

  // -- Queries --
  const rolesQuery = useRolesQuery(orgId);
  const membersQuery = useMembers(orgId);

  // -- Mutations --
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRoleMutation();
  const deleteMutation = useDeleteRoleMutation();

  // -- Derived: map API roles -> component shape, fallback to fixtures --
  const roles = useMemo<ReadonlyArray<Role>>(() => {
    // API data available -- use it
    if (rolesQuery.data && rolesQuery.data.length > 0) {
      return rolesQuery.data.map(apiRoleToComponentRole);
    }
    // Loading or error -- fall back to fixture data so demo mode works
    return RBAC_ROLES;
  }, [rolesQuery.data]);

  // Members: mapped from the real org roster into the lightweight
  // { name, role } shape the RBAC matrix needs. Empty while loading or
  // unavailable — never a fabricated roster (#69, #70).
  const members = useMemo<ReadonlyArray<Member>>(
    () => (membersQuery.data ?? []).map((m) => ({ name: m.name, role: m.role })),
    [membersQuery.data]
  );

  // -- Mutators --

  const addRole = useCallback(
    (draft: RoleDraft): string => {
      const key = generateRoleKey(draft.name);
      if (orgId) {
        // Fire-and-forget: TanStack Query will invalidate the roles list on
        // success, causing a refetch and re-render with the new role.
        createMutation.mutate({
          orgId,
          body: {
            name: draft.name.trim(),
            description: draft.desc.trim() || 'Custom role.',
            permissions: { ...draft.matrix },
          },
        });
      }
      return key;
    },
    [orgId, createMutation]
  );

  const updateRole = useCallback(
    (key: string, draft: RoleDraft): void => {
      // Don't allow editing system roles at the store level
      const existing = roles.find((r) => r.key === key);
      if (!existing || existing.system) return;

      if (orgId) {
        updateMutation.mutate({
          orgId,
          roleKey: key,
          body: {
            name: draft.name.trim(),
            description: draft.desc.trim() || undefined,
            permissions: { ...draft.matrix },
          },
        });
      }
    },
    [orgId, roles, updateMutation]
  );

  const deleteRole = useCallback(
    (key: string): void => {
      // Guard: never delete system roles
      const existing = roles.find((r) => r.key === key);
      if (!existing || existing.system) return;

      if (orgId) {
        deleteMutation.mutate({ orgId, roleKey: key });
      }
    },
    [orgId, roles, deleteMutation]
  );

  const reassignAndDelete = useCallback(
    (key: string, targetKey: string): void => {
      const existing = roles.find((r) => r.key === key);
      if (!existing || existing.system) return;

      if (orgId) {
        deleteMutation.mutate({ orgId, roleKey: key, reassignTo: targetKey });
      }
    },
    [orgId, roles, deleteMutation]
  );

  const value = useMemo<RolesContextValue>(
    () => ({ roles, members, addRole, updateRole, deleteRole, reassignAndDelete }),
    [roles, members, addRole, updateRole, deleteRole, reassignAndDelete]
  );

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

/* --------------------------------- Hook --------------------------------- */

/**
 * Reads the active roles store. Falls back to a read-only snapshot (no
 * mutators) if no provider is mounted, so individual route files can still
 * render in storybook-style tests. Roles fall back to the RBAC_ROLES seed
 * catalog; members are an honest empty array — there is no member fixture to
 * fall back to (#69, #70).
 */
export function useRolesStore(): RolesContextValue {
  const ctx = useContext(RolesContext);
  if (ctx) return ctx;
  console.warn('useRolesStore() called outside <RolesProvider>; using read-only fallback.');
  return {
    roles: RBAC_ROLES,
    members: [],
    addRole: () => '',
    updateRole: () => {},
    deleteRole: () => {},
    reassignAndDelete: () => {},
  };
}
